import { CurrencyType, RefundAttemptDto } from '@tiendanube/common';
import AmountDto from '@tiendanube/common/dist/src/domains/payments/refund/AmountDto';
import { PAYMENTS_CROSS_BORDER } from 'App/featuresFlags';
import useHasTags from 'App/hooks/useHasTags';

export interface RefundCalculationsResult {
  hasSuccessfulRefund: () => boolean;
  calculateRefundedAmount: (defaultCurrency: CurrencyType) => AmountDto;
}

function useRefundCalculations(
  refundAttempts: RefundAttemptDto[],
): RefundCalculationsResult {
  const [hasCrossBorderTag] = useHasTags(PAYMENTS_CROSS_BORDER);

  const isSuccess = (attempt: RefundAttemptDto) =>
    attempt.status.code === 'success';

  const getTotalRefundValue = (attempts: RefundAttemptDto[]) =>
    attempts.reduce((sum, attempt) => sum + getRefundValue(attempt), 0);

  const getRefundValue = (attempt: RefundAttemptDto) =>
    Number(
      (hasCrossBorderTag ? attempt.settled_amount?.value : undefined) ??
        attempt.refunded_amount?.value ??
        '0.00',
    );

  function getCurrency(attempt: RefundAttemptDto) {
    return (
      (hasCrossBorderTag ? attempt.settled_amount?.currency : undefined) ??
      attempt.refunded_amount?.currency
    );
  }

  return {
    hasSuccessfulRefund: () => refundAttempts.some(isSuccess),
    calculateRefundedAmount: (defaultCurrency: CurrencyType) => {
      const successfulAttempts = refundAttempts.filter(isSuccess);

      if (successfulAttempts.length === 0) {
        return {
          value: '0.00',
          currency: defaultCurrency,
        } as AmountDto;
      }

      return {
        value: getTotalRefundValue(successfulAttempts).toFixed(2),
        currency: getCurrency(successfulAttempts[0]) ?? defaultCurrency,
      } as AmountDto;
    },
  };
}

export default useRefundCalculations;
